import { Controller } from "@hotwired/stimulus";

const STORAGE_KEY = "calculator-install-dismissed";
let installPrompt = null;

// The browser can offer installation before Stimulus connects. Keep that one-use
// event across Turbo visits so returning to the calculator can still use it.
window.addEventListener("beforeinstallprompt", (event) => {
  if (!document.querySelector('link[rel="manifest"][href="/calculator/manifest.json"]')) return;

  event.preventDefault();
  installPrompt = event;
  window.dispatchEvent(new Event("calculator-install:change"));
});

window.addEventListener("appinstalled", () => {
  installPrompt = null;
});

export default class extends Controller {
  static targets = ["button", "instructions", "title", "error"];

  connect() {
    this.displayMode = window.matchMedia(
      "(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)"
    );
    this.displayChanged = () => this.refresh();
    this.displayMode.addEventListener("change", this.displayChanged);
    this.close();
    this.refresh();
  }

  disconnect() {
    this.displayMode.removeEventListener("change", this.displayChanged);
  }

  refresh() {
    const standalone = this.displayMode.matches || window.navigator.standalone === true;
    this.element.hidden = standalone || this.wasDismissed() || (!installPrompt && !this.isIOS);
    this.buttonTarget.hidden = false;
    this.errorTarget.hidden = true;
    if (this.element.hidden) this.hideInstructions();

    if (this.isIOS && !installPrompt) {
      this.buttonTarget.setAttribute("aria-controls", this.instructionsTarget.id);
      this.buttonTarget.setAttribute("aria-haspopup", "dialog");
      this.buttonTarget.setAttribute("aria-expanded", String(!this.instructionsTarget.hidden));
    } else {
      this.buttonTarget.removeAttribute("aria-controls");
      this.buttonTarget.removeAttribute("aria-haspopup");
      this.buttonTarget.removeAttribute("aria-expanded");
    }
  }

  async install() {
    if (installPrompt) {
      const prompt = installPrompt;
      installPrompt = null;
      this.refresh();
      try {
        await prompt.prompt();
      } catch {
        if (!this.element.isConnected) return;

        this.element.hidden = false;
        this.buttonTarget.hidden = true;
        this.errorTarget.hidden = false;
      }
    } else if (this.isIOS) {
      if (!this.instructionsTarget.hidden) return this.close();

      this.instructionsTarget.hidden = false;
      this.buttonTarget.setAttribute("aria-expanded", "true");
      this.titleTarget.focus();
    }
  }

  close() {
    if (this.instructionsTarget.hidden) return;

    this.hideInstructions();
    this.buttonTarget.focus();
  }

  hideInstructions() {
    this.instructionsTarget.hidden = true;
    this.buttonTarget.setAttribute("aria-expanded", "false");
  }

  dismiss() {
    this.dismissed = true;
    this.beforeCache();
    try {
      window.localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // Blocked storage still allows dismissal for the current page.
    }
  }

  installed() {
    this.beforeCache();
  }

  beforeCache() {
    this.hideInstructions();
    this.element.hidden = true;
  }

  wasDismissed() {
    if (this.dismissed) return true;

    try {
      return window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  get isIOS() {
    const { userAgent, maxTouchPoints } = window.navigator;
    return /iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  }
}
